import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabaseClient';

interface CounterProps {
  end: number;
  suffix?: string;
  duration?: number;
}

function AnimatedCounter({ end, suffix = '', duration = 1500 }: CounterProps) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    started.current = false;
    setCount(0);
  }, [end]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          if (end === 0) { setCount(0); return; }
          const step = end / (duration / 16);
          let current = 0;
          const timer = setInterval(() => {
            current += step;
            if (current >= end) { setCount(end); clearInterval(timer); }
            else setCount(Math.floor(current));
          }, 16);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={ref}>{count.toLocaleString('es-ES')}{suffix}</span>;
}

interface PlatformStats {
  users: number;
  posts: number;
  leagues: number;
}

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [stats, setStats] = useState<PlatformStats>({ users: 0, posts: 0, leagues: 0 });
  const { isRegistered, openRegisterModal } = useAuth();

  const liveStats = [
    { label: 'Partidos en Directo', value: '247', pulse: true },
    { label: 'Alertas Hoy', value: '38', pulse: true },
    { label: 'Ligas Activas', value: stats.leagues.toString(), pulse: false },
  ];

  // Fetch real platform stats
  useEffect(() => {
    const fetchStats = async () => {
      const [usersRes, postsRes, leaguesRes] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('posts').select('id', { count: 'exact', head: true }),
        supabase.from('leagues').select('id', { count: 'exact', head: true }).eq('status', 'active'),
      ]);
      setStats({
        users: usersRes.count ?? 0,
        posts: postsRes.count ?? 0,
        leagues: leaguesRes.count ?? 0,
      });
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setActiveIndex((i) => (i + 1) % liveStats.length), 2500);
    return () => clearInterval(t);
  }, [stats.leagues]);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dramatic%20nighttime%20football%20stadium%20filled%20with%20crowd%20and%20glowing%20lights%20aerial%20view%20atmospheric%20dark%20sky%20with%20electric%20neon%20green%20and%20orange%20accents%20futuristic%20sports%20arena%20cinematic%20wide%20angle&width=1440&height=900&seq=hero1&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0f14]/80 via-[#0d0f14]/70 to-[#0d0f14]"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0d0f14]/60 via-transparent to-[#0d0f14]/60"></div>
      </div>

      <div className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(#00e87a 1px, transparent 1px), linear-gradient(90deg, #00e87a 1px, transparent 1px)',
          backgroundSize: '80px 80px',
        }}
      ></div>

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 pt-24 pb-16 w-full">
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-[#00e87a]/10 border border-[#00e87a]/30">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00e87a] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00e87a]"></span>
              </span>
              <span className="text-[#00e87a] text-xs font-bold tracking-widest uppercase">En Vivo</span>
            </div>
            <span className="text-white/60 text-xs">·</span>
            <span className="text-white/80 text-sm font-medium">
              {liveStats[activeIndex].value} {liveStats[activeIndex].label}
            </span>
          </div>
        </div>

        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center bg-[#00e87a] rounded-xl">
              <i className="ri-bar-chart-box-line text-black text-2xl"></i>
            </div>
            <span className="text-white font-black text-3xl md:text-4xl tracking-tight">
              Apuesta<span className="text-[#00e87a]">Mente</span>
            </span>
          </div>
        </div>

        <h1 className="text-center text-white font-black uppercase leading-none mb-6">
          <span className="block text-5xl md:text-7xl lg:text-8xl tracking-tight">DOMINA</span>
          <span className="block text-5xl md:text-7xl lg:text-8xl tracking-tight text-[#00e87a]">TUS APUESTAS</span>
          <span className="block text-3xl md:text-4xl lg:text-5xl font-semibold text-white/70 tracking-normal normal-case mt-2">
            con datos reales y comunidad verificada
          </span>
        </h1>

        <p className="text-center text-[#a8b2c1] text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
          Estadísticas avanzadas, ligas competitivas con bankroll virtual y rankings de tipsters verificados por resultados. Todo en una sola plataforma.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          {isRegistered ? (
            <Link
              to="/estadisticas"
              className="px-8 py-4 bg-[#00e87a] text-black text-base font-black rounded-md hover:bg-[#00d070] transition-all hover:scale-105 whitespace-nowrap cursor-pointer uppercase tracking-wide"
            >
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-rocket-line text-lg"></i>
                </div>
                Ir a Estadísticas
              </span>
            </Link>
          ) : (
            <button
              onClick={openRegisterModal}
              className="px-8 py-4 bg-[#00e87a] text-black text-base font-black rounded-md hover:bg-[#00d070] transition-all hover:scale-105 whitespace-nowrap cursor-pointer uppercase tracking-wide"
            >
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-rocket-line text-lg"></i>
                </div>
                Comenzar Gratis
              </span>
            </button>
          )}
          <Link
            to="/comparador"
            className="px-8 py-4 border border-white/30 text-white text-base font-semibold rounded-md hover:bg-white/10 hover:border-white/60 transition-all whitespace-nowrap cursor-pointer"
          >
            <span className="flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-scales-3-line text-lg"></i>
              </div>
              Ver Cuotas en Directo
            </span>
          </Link>
        </div>

        {/* Platform stats — 100% real from Supabase */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {[
            { value: stats.users,   suffix: '',   label: 'Apostadores registrados', icon: 'ri-group-line' },
            { value: stats.posts,   suffix: '',   label: 'Pronósticos publicados',   icon: 'ri-check-double-line' },
            { value: stats.users,   suffix: '',   label: 'Tipsters activos',          icon: 'ri-user-star-line' },
            { value: stats.leagues, suffix: '',   label: 'Ligas competitivas',        icon: 'ri-trophy-line' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center hover:border-[#00e87a]/30 transition-all"
            >
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2 text-[#00e87a]">
                <i className={`${stat.icon} text-xl`}></i>
              </div>
              <p className="text-white text-2xl font-black tabular-nums">
                <AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-[#6b7280] text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-5 h-5 flex items-center justify-center text-white/30">
          <i className="ri-arrow-down-s-line text-2xl"></i>
        </div>
      </div>
    </section>
  );
}
